'use client';
import { createContext, useContext, useMemo } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import {
  createOffersFilterStore,
  type OffersFilterStore,
  type OffersFilterStoreState,
} from './store';
import type { FilterEnabledSlugs } from './types';

export const OffersFilterStoreContext = createContext<OffersFilterStore | null>(null);

export function OffersFilterStoreProvider({
  offers,
  slug,
  children,
}: {
  offers: ProductCardType[];
  slug: FilterEnabledSlugs;
  children: React.ReactNode;
}) {
  const store = useMemo(() => createOffersFilterStore(offers, slug), [offers, slug]);

  return (
    <OffersFilterStoreContext.Provider value={store}>{children}</OffersFilterStoreContext.Provider>
  );
}

export function useOffersFilterStore<T>(
  selector: (state: OffersFilterStoreState) => T,
  equalityFn?: (left: T, right: T) => boolean,
): T {
  const store = useContext(OffersFilterStoreContext);
  if (!store) throw new Error('Missing OffersFilterStoreProvider in the tree');

  return useStoreWithEqualityFn(store, selector, equalityFn);
}

export {
  useFilterLocationOptions,
  useFilterProductTypeOptions,
  useGetOffersFilterOffers,
} from './hooks';
